import { SupportedLanguages } from '@constants/constants.type';
import { COUNTRIES, Countries } from '@constants/countries';
import { LANGAUAGE_PRIORITY, PLACEHOLDER_IMAGE } from '@constants/global';
import {
  COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME,
  COMPANY_BANKING_DETAILS_BANK_CODE,
  COMPANY_BANKING_DETAILS_BANK_NUMBER,
  COMPANY_BANKING_DETAILS_COUNTRY,
  COMPANY_BILLING_INFO_CITY,
  COMPANY_BILLING_INFO_CODICE_FISCALE,
  COMPANY_BILLING_INFO_COUNTRY,
  COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON,
  COMPANY_BILLING_INFO_LEGAL_NAME,
  COMPANY_BILLING_INFO_POSTCODE,
  COMPANY_BILLING_INFO_REGISTERED_ADDRESS,
  COMPANY_BILLING_INFO_VAT_NUMBER,
  COMPANY_FINANCE_NOTIFICATIONS_EMAILS,
  COMPANY_FINANCE_NOTIFICATIONS_PHONE,
  COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED,
  COMPANY_SETTING_BANKING_DETAILS,
  COMPANY_SETTING_BILLING_INFO,
  COMPANY_SETTING_CITIES,
  COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS,
  COMPANY_SETTING_DESCRIPTION,
  COMPANY_SETTING_DESCRIPTION_TRANSLATIONS,
  COMPANY_SETTING_FINANCE_NOTIFICATIONS,
  COMPANY_SETTING_IS_PUBLIC_PROFILE,
  COMPANY_SETTING_LOGO_URL,
  COMPANY_SETTING_MUSIC_GENRES,
  COMPANY_SETTING_PUBLIC_NAME,
  COMPANY_SETTING_PUBLIC_PROFILE,
  COMPANY_SETTING_VIBES,
  COMPANY_SETTINGS_COUNTRY,
  COMPANY_SETTINGS_COUNTRY_ISO_CODE,
  COMPANY_SETTINGS_IMAGE_INPUT_NAME,
  CompanyApiTranslations,
  CompanyDescriptionTranslations,
  CompanyPublicProfileRequest,
  CompanySettings,
  CompanySettingsFormTypes,
} from '@domain/company-settings';
import { CompanyCountry } from '@hooks/company/use-get-countries';
import { clearEmptyValues } from '@utils/clear-empty-fields';

import { CreateCompanyFieldProps, CreateFieldProps, FieldConfig } from '../company-settings-form.type';

export const SEPA_COUNTRIES = [
  'IT', // Italy
  'ES', // Spain
  'FR', // France
  'PT', // Portugal
  'DK', // Denmark
  'LU', // Luxembourg
  'BE', // Belgium
  'DE', // Germany
  'NL', // Netherlands
  'IE', // Ireland
  'SI', // Slovenia
  'GR', // Greece
  'BG', // Bulgaria
  'MC', // Monaco
  'AT', // Austria
  'FI', // Finland
  'CY', // Cyprus
  'EE', // Estonia
  'LV', // Latvia
  'LT', // Lithuania
  'MT', // Malta
  'PL', // Poland
  'CZ', // Czech Republic
  'SK', // Slovakia
  'HU', // Hungary
  'SE', // Sweden
  'HR', // Croatia
  'RO', // Romania
  'CH', // Switzerland
  'AD', // Andorra
];

export const createField = ({
  id,
  t,
  extraProps,
  dataType,
  isRequired = false,
  labelKey,
  options,
  disabled = false,
  readOnly = false,
}: CreateFieldProps): FieldConfig => ({
  id,
  name: labelKey ? t(labelKey) : '',
  slug: id,
  dataType,
  isRequired,
  disabled,
  readOnly,
  ...(options && { options }),
  ...(extraProps && { extraProps }),
});

export const createCompanyField = ({
  id,
  t,
  extraProps,
  dataType,
  labelKey,
  isRequired = false,
  disabled = false,
  readOnly = false,
}: CreateCompanyFieldProps) => createField({ id, t, dataType, isRequired, labelKey, disabled, readOnly, extraProps });

export const isCountrySEPA = (country?: string) => (country ? SEPA_COUNTRIES.includes(country) : false);

export const formatCompanyCountries = (companyCountries: CompanyCountry[]): Countries => {
  const orderedCountries = new Map(
    companyCountries.map((country) => [
      country.isoCode,
      {
        id: country.id,
        label: country.name,
        isoCode: country.isoCode,
        dialCode: COUNTRIES[country.isoCode]?.dialCode || '',
      },
    ])
  );

  return Object.fromEntries(orderedCountries);
};

export const getCountryId = ({ country, countries }: { country: string; countries?: Countries }) => {
  return countries?.[country].id ?? '';
};

export const getCardDisclosureDescription = ({
  billingInfo,
  bankingDetails,
  warningBillingInfo,
  warningBankingFields,
}: {
  billingInfo: Record<string, string>;
  bankingDetails: Record<string, string>;
  warningBillingInfo: string;
  warningBankingFields: string;
}) => {
  const hasVatNumber = billingInfo[COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON] === 'false';
  const isItalianCompany = billingInfo.country === 'IT';

  const hasEmptyBillingFields = Object.keys(billingInfo).some((key) => {
    const value = billingInfo[key];
    if (key === COMPANY_BILLING_INFO_VAT_NUMBER) return hasVatNumber && !value;
    if (key === COMPANY_BILLING_INFO_CODICE_FISCALE) return hasVatNumber && isItalianCompany && !value;

    return !value;
  });
  const hasEmptyBankingFields = Object.values(bankingDetails).some((value) => !value);

  if (!hasEmptyBillingFields && !hasEmptyBankingFields) {
    return '';
  }

  if (hasEmptyBillingFields && hasEmptyBankingFields) {
    return `${warningBillingInfo}\n${warningBankingFields}`;
  }

  if (hasEmptyBillingFields) {
    return warningBillingInfo;
  }

  return warningBankingFields;
};

// The backend stores every multi-email field as an array; the textarea holds a
// comma-separated string. `null` is the cleared value the PATCH expects.
// filter(Boolean) drops the empty segment a trailing or doubled comma leaves behind — the BE
// 400s on any invalid address. multiEmail validation already rejects those before Save enables,
// but this helper is shared with financeNotifications, so it does not rely on the caller.
const toEmailList = (value: string) =>
  value.trim() === '' ? null : value.replace(/\s+/g, '').split(',').filter(Boolean);

// The form validates some fields programmatically on mount (the trigger() calls in
// company-settings-form), so `errors` can be populated for data the user never touched —
// gating Save on it raw would lock out a company whose stored IBAN no longer validates.
// trigger() does not mark a field touched, but the field onChange does, so only errors on
// fields the user actually edited count.
export const hasTouchedFieldError = (errors: object, touchedFields: object): boolean =>
  Object.entries(touchedFields).some(([key, isTouched]) => {
    const error = (errors as Record<string, unknown>)[key];
    if (!error) return false;
    if (isTouched === true) return true;

    return typeof isTouched === 'object' && hasTouchedFieldError(error as object, isTouched as object);
  });

/**
 * Splits the API's per-locale map into the two controls the form has for it: the base locale's copy
 * feeds the plain description field, everything else feeds the translations block.
 *
 * A locale the Pro UI does not offer is dropped rather than surfaced, since there is no textarea to
 * render it in. The company description is only ever written from here, so that set is empty in
 * practice — the filter is what makes `areTranslationsEnabled` mean "the form can show them".
 */
export const splitDescriptionTranslations = (
  translations: CompanyApiTranslations = {},
  baseLocale?: SupportedLanguages
) => {
  const descriptionTranslations: CompanyDescriptionTranslations = {};

  for (const locale of LANGAUAGE_PRIORITY) {
    if (locale === baseLocale) continue;
    const description = translations[locale]?.description ?? '';
    if (description !== '') {
      descriptionTranslations[locale] = description;
    }
  }

  return {
    [COMPANY_SETTING_DESCRIPTION]: (baseLocale ? translations[baseLocale]?.description : undefined) ?? '',
    [COMPANY_SETTING_DESCRIPTION_TRANSLATIONS]: descriptionTranslations,
    [COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED]: Object.keys(descriptionTranslations).length > 0,
  };
};

export const getDefaultValues = ({
  companyInfo,
  baseLocale,
}: {
  companyInfo?: CompanySettings;
  baseLocale?: SupportedLanguages;
}) => {
  const publicProfile = companyInfo?.[COMPANY_SETTING_PUBLIC_PROFILE];

  return {
    [COMPANY_SETTING_PUBLIC_NAME]: companyInfo?.[COMPANY_SETTING_PUBLIC_NAME] ?? '',
    [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]: companyInfo?.[COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]?.join(',') ?? '',
    ...splitDescriptionTranslations(publicProfile?.translations, baseLocale),
    [COMPANY_SETTING_CITIES]: publicProfile?.[COMPANY_SETTING_CITIES]?.map((city) => city.id) ?? [],
    [COMPANY_SETTING_MUSIC_GENRES]: publicProfile?.[COMPANY_SETTING_MUSIC_GENRES]?.map((genre) => genre.id) ?? [],
    [COMPANY_SETTING_VIBES]: publicProfile?.[COMPANY_SETTING_VIBES]?.map((vibe) => vibe.id) ?? [],
    [COMPANY_SETTING_IS_PUBLIC_PROFILE]: publicProfile?.[COMPANY_SETTING_IS_PUBLIC_PROFILE] ?? false,
    [COMPANY_BILLING_INFO_COUNTRY]:
      companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_SETTINGS_COUNTRY]?.[COMPANY_SETTINGS_COUNTRY_ISO_CODE] ?? '',
    [COMPANY_BANKING_DETAILS_COUNTRY]:
      companyInfo?.[COMPANY_SETTING_BANKING_DETAILS][COMPANY_SETTINGS_COUNTRY]?.[COMPANY_SETTINGS_COUNTRY_ISO_CODE] ??
      '',
    [COMPANY_SETTING_LOGO_URL]: companyInfo?.[COMPANY_SETTING_LOGO_URL] ?? PLACEHOLDER_IMAGE,
    [COMPANY_SETTING_BILLING_INFO]: {
      [COMPANY_BILLING_INFO_LEGAL_NAME]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_LEGAL_NAME] ?? '',
      [COMPANY_BILLING_INFO_REGISTERED_ADDRESS]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_REGISTERED_ADDRESS] ?? '',
      [COMPANY_BILLING_INFO_POSTCODE]: companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_POSTCODE] ?? '',
      [COMPANY_BILLING_INFO_CITY]: companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_CITY] ?? '',
      [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]?.toString() ?? 'false',
      [COMPANY_BILLING_INFO_VAT_NUMBER]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_VAT_NUMBER] ?? '',
      [COMPANY_BILLING_INFO_CODICE_FISCALE]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_CODICE_FISCALE] ?? '',
    },
    [COMPANY_SETTING_BANKING_DETAILS]: {
      [COMPANY_BANKING_DETAILS_BANK_NUMBER]:
        companyInfo?.[COMPANY_SETTING_BANKING_DETAILS][COMPANY_BANKING_DETAILS_BANK_NUMBER] ?? '',
      [COMPANY_BANKING_DETAILS_BANK_CODE]:
        companyInfo?.[COMPANY_SETTING_BANKING_DETAILS][COMPANY_BANKING_DETAILS_BANK_CODE] ?? '',
      [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_LEGAL_NAME] ?? '',
    },
    [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: {
      [COMPANY_FINANCE_NOTIFICATIONS_PHONE]:
        companyInfo?.[COMPANY_SETTING_FINANCE_NOTIFICATIONS][COMPANY_FINANCE_NOTIFICATIONS_PHONE] ?? '',
      [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]:
        companyInfo?.[COMPANY_SETTING_FINANCE_NOTIFICATIONS][COMPANY_FINANCE_NOTIFICATIONS_EMAILS]?.join(',') ?? '',
    },
  };
};

/**
 * The `publicProfile` block of the PATCH, built from the keys the user actually touched.
 *
 * Values are read from the full form rather than from `formDirty`, because the dirty slice of a
 * collection is not the collection: react-hook-form marks a multi-select dirty per index, and
 * `translations` has to go out whole anyway (see `CompanyPublicProfileRequest`).
 *
 * `areTranslationsEnabled` never leaves the form. Off means "no translations", which the API
 * expresses as every non-base locale blank — the same rule the offer and add-on forms use.
 */
const getPublicProfileFromForm = ({
  formDirty,
  values,
  baseLocale,
}: {
  formDirty: Partial<CompanySettingsFormTypes>;
  values: Partial<CompanySettingsFormTypes>;
  baseLocale: SupportedLanguages;
}): CompanyPublicProfileRequest => {
  const isDirty = (key: keyof CompanySettingsFormTypes) => Object.hasOwn(formDirty, key);
  const publicProfile: CompanyPublicProfileRequest = {};

  if (
    isDirty(COMPANY_SETTING_DESCRIPTION) ||
    isDirty(COMPANY_SETTING_DESCRIPTION_TRANSLATIONS) ||
    isDirty(COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED)
  ) {
    const areTranslationsEnabled = values[COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED] ?? false;
    const translations: CompanyApiTranslations = {
      [baseLocale]: { description: values[COMPANY_SETTING_DESCRIPTION] ?? '' },
    };

    for (const locale of LANGAUAGE_PRIORITY) {
      if (locale === baseLocale) continue;
      const description = areTranslationsEnabled
        ? values[COMPANY_SETTING_DESCRIPTION_TRANSLATIONS]?.[locale] ?? ''
        : '';
      // A blank description is how the API is told to drop the locale, and `clearEmptyValues`
      // turns it into the `null` the extractor reads as such.
      translations[locale] = { description };
    }

    publicProfile.translations = translations;
  }

  if (isDirty(COMPANY_SETTING_CITIES)) {
    publicProfile[COMPANY_SETTING_CITIES] = values[COMPANY_SETTING_CITIES] ?? [];
  }

  if (isDirty(COMPANY_SETTING_MUSIC_GENRES)) {
    publicProfile[COMPANY_SETTING_MUSIC_GENRES] = values[COMPANY_SETTING_MUSIC_GENRES] ?? [];
  }

  if (isDirty(COMPANY_SETTING_VIBES)) {
    publicProfile[COMPANY_SETTING_VIBES] = values[COMPANY_SETTING_VIBES] ?? [];
  }

  if (isDirty(COMPANY_SETTING_IS_PUBLIC_PROFILE)) {
    publicProfile[COMPANY_SETTING_IS_PUBLIC_PROFILE] = values[COMPANY_SETTING_IS_PUBLIC_PROFILE] ?? false;
  }

  return publicProfile;
};

export const getCompanySettingsFromForm = ({
  formDirty,
  companyCountries,
  publicProfileContext,
}: {
  formDirty: Partial<CompanySettingsFormTypes>;
  companyCountries?: Countries;
  /**
   * Present only when the organizer flag is on. Absent, the public-profile keys are dropped rather
   * than mapped — with the flag off none of them is registered, so none can be dirty either.
   */
  publicProfileContext?: { values: Partial<CompanySettingsFormTypes>; baseLocale: SupportedLanguages };
}) => {
  const {
    [COMPANY_SETTINGS_IMAGE_INPUT_NAME]: _logoUrl,
    [COMPANY_BILLING_INFO_COUNTRY]: billingCountry,
    [COMPANY_BANKING_DETAILS_COUNTRY]: bankCountry,
    // The organizer fields are front-end form keys with no top-level API counterpart: they leave
    // nested under `publicProfile`, and `areTranslationsEnabled` does not leave at all. Sending one
    // flat would fail the whole PATCH, taking any billing or banking edit down with it.
    [COMPANY_SETTING_DESCRIPTION]: _description,
    [COMPANY_SETTING_DESCRIPTION_TRANSLATIONS]: _descriptionTranslations,
    [COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED]: _areTranslationsEnabled,
    [COMPANY_SETTING_CITIES]: _cities,
    [COMPANY_SETTING_MUSIC_GENRES]: _musicGenres,
    [COMPANY_SETTING_VIBES]: _vibes,
    [COMPANY_SETTING_IS_PUBLIC_PROFILE]: _isPublicProfile,
    ...formSettings
  } = formDirty;

  const publicProfile = publicProfileContext
    ? getPublicProfileFromForm({
        formDirty,
        values: publicProfileContext.values,
        baseLocale: publicProfileContext.baseLocale,
      })
    : {};

  // Remove the read-only accountHolderName field from banking details if it exists
  if (formSettings[COMPANY_SETTING_BANKING_DETAILS]?.[COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME] !== undefined) {
    const { [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]: accountHolderName, ...restBankingDetails } =
      formSettings[COMPANY_SETTING_BANKING_DETAILS];
    formSettings[COMPANY_SETTING_BANKING_DETAILS] = restBankingDetails;
  }

  const customerSupportEmails = formSettings[COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS];

  const transformedSettings = {
    ...formSettings,
    ...(customerSupportEmails !== undefined && {
      [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]: toEmailList(customerSupportEmails),
    }),
    // Unlike the three sections below, this one is omitted when empty: it is additive to an
    // endpoint that predates it, and an empty object would read as "the block was touched".
    ...(Object.keys(publicProfile).length > 0 && { [COMPANY_SETTING_PUBLIC_PROFILE]: publicProfile }),
    [COMPANY_SETTING_BILLING_INFO]: {
      ...formSettings[COMPANY_SETTING_BILLING_INFO],
      ...(billingCountry && {
        countryId: getCountryId({ country: billingCountry, countries: companyCountries }),
      }),
      ...(formSettings[COMPANY_SETTING_BILLING_INFO] &&
        COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON in formSettings[COMPANY_SETTING_BILLING_INFO] && {
          [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]:
            formSettings[COMPANY_SETTING_BILLING_INFO]?.[COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON] === 'true',
        }),
    },
    [COMPANY_SETTING_BANKING_DETAILS]: {
      ...formSettings[COMPANY_SETTING_BANKING_DETAILS],
      ...(bankCountry && {
        countryId: getCountryId({ country: bankCountry, countries: companyCountries }),
      }),
    },
    [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: {
      ...formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS],
      ...(formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS] &&
        COMPANY_FINANCE_NOTIFICATIONS_EMAILS in formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS] && {
          [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]: toEmailList(
            formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS][COMPANY_FINANCE_NOTIFICATIONS_EMAILS]
          ),
        }),
    },
  };

  return clearEmptyValues(transformedSettings);
};

/**
 * Whether a transformed body carries anything the API would act on.
 *
 * `getCompanySettingsFromForm` always emits `billingInfo`, `bankingDetails` and
 * `financeNotifications`, so the body is never literally empty — a change to a form key that is
 * stripped before the PATCH still produces three empty objects, which would be sent and answered
 * with a success toast for a request that changed nothing.
 *
 * A cleared field survives this: `clearEmptyValues` turns it into `null`, which is a value.
 */
const isEmptyBranch = (value: unknown): boolean =>
  !!value && typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(isEmptyBranch);

export const hasSettingsToSave = (settings: object): boolean =>
  Object.values(settings).some((value) => !isEmptyBranch(value));
